import {useCallback, useEffect} from "react";
import {useLocation, useNavigate} from "react-router";
import {useAuth0} from "@auth0/auth0-react";
import {useStores} from "../../stores";
import {routes} from "../../routing/routes";
import type {LoginRouteState} from "../routing/LoginRouteState";
import {hasAuthCallbackParams} from "../routing/LoginRouteState";
import {completeAuth0MithraLogin, getAuth0CallbackSearchError, startAuth0Login} from "./Auth0Login";

export type UseAuth0LoginOptions = {
    loginIntention: LoginRouteState | null;
    autoLogin?: boolean;
};

/** Must be rendered under Auth0Provider (when environment.hasAuth0). */
export function useAuth0Login({loginIntention, autoLogin = false}: UseAuth0LoginOptions) {
    const {authStore} = useStores();
    const navigate = useNavigate();
    const location = useLocation();
    const auth0Context = useAuth0();
    const {isAuthenticated, isLoading, error: auth0SdkError} = auth0Context;

    const mithraUser = authStore.user;
    const isMithraLoggingIn = authStore.authentication.isMithraLoggingIn;
    const loginFailed = authStore.authentication.alert?.severity === 'error';
    const isLoggingIn = authStore.authentication.isLoggingIn === 'auth0';
    const callbackError = getAuth0CallbackSearchError(location.search);
    const isMithraSyncing = isAuthenticated && !mithraUser;

    const login = useCallback(() => {
        console.debug('Auth0Login: starting login');
        startAuth0Login(authStore, auth0Context, loginIntention);
    }, [auth0Context, authStore, loginIntention]);

    useEffect(() => {
        if (isLoading) return;

        if (callbackError || auth0SdkError) {
            const message = callbackError?.message
                || auth0SdkError?.message
                || 'Login failed, please try again';
            authStore.authentication.processMithraLoginError(message);
            if (location.pathname !== routes.login) {
                navigate(routes.login, {replace: true});
            } else if (callbackError) {
                navigate(location.pathname, {replace: true, state: location.state});
            }
            return;
        }

        if (loginFailed) return;

        if (isAuthenticated && !mithraUser && !isMithraLoggingIn) {
            completeAuth0MithraLogin(authStore, auth0Context, location, navigate)
                .catch(() => {
                    if (location.pathname !== routes.login) {
                        navigate(routes.login, {replace: true});
                    }
                });
        }
    }, [
        auth0Context,
        auth0SdkError,
        authStore,
        callbackError,
        isAuthenticated,
        isLoading,
        isMithraLoggingIn,
        location,
        loginFailed,
        mithraUser,
        navigate,
    ]);

    useEffect(() => {
        if (!autoLogin || isLoading || loginFailed || hasAuthCallbackParams(location.search)) return;

        if (!isAuthenticated && !mithraUser && !isMithraLoggingIn && !isLoggingIn) {
            login();
        }
    }, [
        autoLogin,
        isAuthenticated,
        isLoading,
        isLoggingIn,
        location.search,
        login,
        loginFailed,
        mithraUser,
        isMithraLoggingIn,
    ]);

    return {
        login,
        isAuthenticated,
        isLoading,
        isLoggingIn,
        isMithraSyncing,
        loginFailed,
    };
}
